// Chronicles and portraits published from the atelier, served by the tools
// service to the readers of a tree (GET /stories/published…): the same rule
// as the transcriptions, the caller's Gramps token decides which tree.

import { reportUnauthorized } from './grampsApi';

/** A 401 for a token that has just gone stale: it is being refreshed, and
 * the request is worth asking again. */
export class StaleTokenError extends Error {}
import { getToolsApiBase } from './ocr';

export interface PublishedStory {
    id: string;
    kind: 'chronicle' | 'portrait' | 'migration';
    /** A migration's places of departure. */
    from?: string[] | null;
    title: string | null;
    published_at: string | null;
    /** The place of a chronicle, the person of a portrait. */
    subject: string | null;
    /** The person of the present the story is tied to. */
    anchor: string | null;
    /** Gramps id of the anchor person, so the reader can open their ficha. */
    anchor_id?: string | null;
    from_year?: number | null;
    to_year?: number | null;
    /** Gramps ids of the people the story concerns. */
    people: string[];
    /** In a list: the opening, as plain text. */
    excerpt?: string;
    /** On its page: the text, in Markdown, and its sources. */
    body?: string | null;
    /** The language model that wrote the text. */
    model?: string | null;
    /** Numbered as the text cites them, S1 first. `site` is the page's site. */
    sources?: { title?: string; url?: string; site?: string }[];
    /** On its page: the story read aloud, a link good for a few hours. */
    audio?: { url: string; seconds: number | null } | null;
    /** In a list: whether it can be listened to. */
    has_audio?: boolean;
    /** Signed URL of the story's WebP illustration, good for a few hours. */
    image?: string | null;
    /** Signed URL of where the story happens, on a map (made on first
     * asking; a 404 when there is nowhere to show). */
    map?: string | null;
}

function headers(token?: string): Record<string, string> {
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchPublishedStories(token?: string, person?: string): Promise<PublishedStory[]> {
    const query = person ? `?${new URLSearchParams({ person })}` : '';
    const response = await fetch(`${getToolsApiBase()}/stories/published${query}`, { headers: headers(token) });
    // A stale token (it lives 15 minutes) is refreshed, and the list asked
    // again with the new one; were it an empty list, it would be kept as if
    // there were nothing to read.
    if (response.status === 401) {
        reportUnauthorized(token);
        throw new StaleTokenError('Stories request refused: the token is stale');
    }
    // No access is not an error for a reader: there is just nothing to read.
    if (response.status === 403) return [];
    if (!response.ok) throw new Error(`Stories request failed: HTTP ${response.status}`);
    return ((await response.json()) as { stories: PublishedStory[] }).stories;
}

export async function fetchPublishedStory(id: string, token?: string): Promise<PublishedStory | null> {
    const response = await fetch(`${getToolsApiBase()}/stories/published/${encodeURIComponent(id)}`, {
        headers: headers(token),
    });
    if (response.status === 401) {
        reportUnauthorized(token);
        throw new StaleTokenError('Story request refused: the token is stale');
    }
    if (response.status === 404 || response.status === 403) return null;
    if (!response.ok) throw new Error(`Story request failed: HTTP ${response.status}`);
    return (await response.json()) as PublishedStory;
}

/** "Saint-Jean-aux-Bois, 1780–1820" or the person of a portrait. */
export function storySubtitle(story: PublishedStory): string {
    if (story.kind === 'migration') {
        return `${(story.from ?? []).join(' ; ')} → ${story.subject ?? ''}, ${story.from_year}–${story.to_year}`;
    }
    if (story.kind === 'chronicle' && story.from_year && story.to_year) {
        return `${story.subject ?? ''}, ${story.from_year}–${story.to_year}`;
    }
    return story.subject ?? '';
}

/** Display a Gramps person label ("Surname, Given") in natural French order. */
export function storyAnchorLabel(anchor: string | null | undefined): string | null {
    const trimmed = anchor?.trim();
    if (!trimmed) return null;
    const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length === 2) return `${parts[1]} ${parts[0]}`;
    return trimmed;
}

/** Human-friendly phrase naming the model that wrote a story. */
export function storyModelLabel(model: string | null | undefined): string {
    const trimmed = model?.trim();
    if (!trimmed) return 'un modèle de langage';
    const lower = trimmed.toLowerCase();
    if (lower.startsWith('gemini-')) return `le modèle Gemini (${trimmed})`;
    if (lower.startsWith('claude-')) return `le modèle Claude (${trimmed})`;
    if (lower.startsWith('mistral-')) return `le modèle Mistral (${trimmed})`;
    return `le modèle ${trimmed}`;
}

/** Where a story's audio or image loads from: the tools service, or this very site
 * on marchand-viveret.eu. */
export function audioSource(url: string): string {
    return `${getToolsApiBase()}${url}`;
}

export function imageSource(url: string): string {
    return `${getToolsApiBase()}${url}`;
}
